import React, { useEffect, useState } from "react";
import { analyticsService } from "@/api/services";
import { TrendUp, Trophy, Fire, ChartLineUp } from "@phosphor-icons/react";

/**
 * Personal hit-rate / ROI / streak tile for the dashboard.
 * Numbers reflect picks visible at the user's tier over the last 30 days.
 */
export default function DashboardAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        analyticsService
            .me(30)
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn("Analytics load failed:", err?.message || err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <section
                className="max-w-7xl mx-auto px-4 md:px-8"
                data-testid="dashboard-analytics-loading"
            >
                <div className="border border-border bg-card p-6 h-40 pulse-sharp">
                    <div className="font-mono text-xs uppercase tracking-widest text-zinc-600">
                        Crunching your hit-rate…
                    </div>
                </div>
            </section>
        );
    }
    if (!data) return null;

    const o = data.overall || {};
    const s = data.streaks || {};
    const roiPos = (o.roi || 0) >= 0;

    return (
        <section
            className="max-w-7xl mx-auto px-4 md:px-8"
            data-testid="dashboard-analytics"
        >
            <div className="flex items-baseline justify-between mb-4 border-b border-border pb-3">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                    Your numbers
                </h2>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    LAST {data.days || 30} DAYS · {(data.tier || "basic").toUpperCase()}
                </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat
                    icon={<TrendUp size={20} weight="duotone" />}
                    label="Hit rate"
                    value={`${(o.hitRate || 0).toFixed(1)}%`}
                    sub={`${o.won || 0}W / ${o.lost || 0}L`}
                    testId="analytics-hit-rate"
                    accent
                />
                <Stat
                    icon={<ChartLineUp size={20} weight="duotone" />}
                    label="ROI (1u/pick)"
                    value={`${roiPos ? "+" : ""}${(o.roi || 0).toFixed(1)}%`}
                    sub={`${o.total || 0} settled picks`}
                    testId="analytics-roi"
                    negative={!roiPos && (o.roi || 0) < 0}
                />
                <Stat
                    icon={<Fire size={20} weight="duotone" />}
                    label="Current streak"
                    value={`${s.currentStreak || 0}W`}
                    sub="back-to-back wins"
                    testId="analytics-current-streak"
                />
                <Stat
                    icon={<Trophy size={20} weight="duotone" />}
                    label="Best streak"
                    value={`${s.longestWinStreak || 0}W`}
                    sub="all-time peak"
                    testId="analytics-best-streak"
                    accent
                />
            </div>
        </section>
    );
}

function Stat({ icon, label, value, sub, accent, negative, testId }) {
    return (
        <div
            className={`bg-card border ${accent ? "border-primary/60" : "border-border"} p-5`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 mb-2 text-primary">
                {icon}
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {label}
                </div>
            </div>
            <div
                className={`font-mono text-3xl font-bold ${
                    negative ? "text-red-400" : accent ? "text-primary" : "text-white"
                }`}
            >
                {value}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                {sub}
            </div>
        </div>
    );
}
